import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { siteConfig } from '../data/mock';

const Hero = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1549041050-386c1c99d655?auto=format&fit=crop&w=2000&q=80)'
        }}
      />
      
      {/* Navy/Purple gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, rgba(31, 59, 96, 0.92) 0%, rgba(45, 55, 95, 0.88) 40%, rgba(75, 60, 100, 0.85) 70%, rgba(120, 80, 110, 0.80) 100%)'
        }}
      />
      
      {/* Additional subtle overlay for depth */}
      <div className="absolute inset-0">
        {/* Subtle gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 30% 20%, rgba(42, 74, 117, 0.3) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(214, 169, 79, 0.08) 0%, transparent 50%)'
          }}
        />
        {/* Decorative elements */}
        <div
          className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-float"
          style={{ background: 'rgba(214, 169, 79, 0.06)' }}
        />
        <div
          className="absolute bottom-1/4 left-1/4 w-72 h-72 rounded-full blur-3xl animate-float"
          style={{ background: 'rgba(42, 74, 117, 0.15)', animationDelay: '2s' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          {/* Main headline */}
          <h1
            className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-white mb-8 animate-fade-in-up stagger-1"
          >
            Pacific Crest Properties
          </h1>

          {/* Slogan - now white/light instead of gold */}
          <p
            className="text-2xl sm:text-3xl lg:text-4xl font-display font-medium mb-2 animate-fade-in-up stagger-2 text-white/90"
          >
            {siteConfig.slogan}
          </p>
          <p
            className="text-2xl sm:text-3xl lg:text-4xl font-display font-medium mb-10 animate-fade-in-up stagger-2 text-white/90"
          >
            {siteConfig.sloganLine2}
          </p>

          {/* Supporting text */}
          <p
            className="max-w-3xl mx-auto text-lg sm:text-xl text-white/70 leading-relaxed mb-12 animate-fade-in-up stagger-3"
          >
            A modern real estate solutions company helping Los Angeles homeowners improve, sell, or transform their properties with clarity and confidence.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up stagger-4">
            <Link
              to={siteConfig.links.offers}
              className="btn-gold group flex items-center space-x-2 text-lg animate-pulse-glow"
            >
              <span>Get Your Offer</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={siteConfig.links.renovations}
              className="btn-outline-gold group flex items-center space-x-2 text-lg"
            >
              <Sparkles className="w-5 h-5" />
              <span>Explore Renovation Services</span>
            </Link>
          </div>

          {/* Trust indicators - Value Pillars */}
          <div className="mt-20 animate-fade-in-up stagger-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="glass rounded-2xl px-5 py-4 text-center group hover:bg-white/10 transition-all duration-300">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <svg className="w-5 h-5" style={{ color: '#1F3B60' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-white/90 block">Verified Local Buyer</span>
              </div>
              
              <div className="glass rounded-2xl px-5 py-4 text-center group hover:bg-white/10 transition-all duration-300">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <svg className="w-5 h-5" style={{ color: '#1F3B60' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-white/90 block">Licensed & Insured</span>
              </div>
              
              <div className="glass rounded-2xl px-5 py-4 text-center group hover:bg-white/10 transition-all duration-300">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <svg className="w-5 h-5" style={{ color: '#1F3B60' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-white/90 block">Transparent Pricing</span>
              </div>
              
              <div className="glass rounded-2xl px-5 py-4 text-center group hover:bg-white/10 transition-all duration-300">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <svg className="w-5 h-5" style={{ color: '#1F3B60' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-white/90 block">No Pressure</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-40"
        style={{ background: 'linear-gradient(to top, #1F3B60, transparent)' }}
      />
    </section>
  );
};

export default Hero;
